/**
 * Detailed coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const detailedColoringPagesPack: ToilePack = {
    slug: 'detailed-coloring-pages',
    category: 'coloring',
    name: 'Detailed coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'owl', label: 'Zentangle owl', subject: 'an owl on a branch, every feather a different tangle pattern of scales, spirals, grids and tiny circles'},
        {key: 'street', label: 'Old town street', subject: 'a narrow cobbled street of tall houses with shutters, flower boxes, shop signs without words and a cat on a step'},
        {key: 'ocean', label: 'Ocean reef', subject: 'a coral reef with sea turtles, fish, seahorses, anemones and shells filling every corner'},
    ],
    styles: [
        {key: 'fine', label: 'Hair-thin line', style: 'hair-thin uniform black line, hundreds of tiny closed cells sized for fine-tip pens', palette: 'black ink only', framing: 'The drawing filling the page with a thin white margin'},
        {key: 'tangle', label: 'Tangled fills', style: 'thin black line, every area filled with a different repeating tangle pattern', palette: 'black ink only', framing: 'The drawing filling the page with a thin white margin'},
        {key: 'crowded', label: 'Crowded', style: 'crowded maximalist drawing in thin and medium black line, every corner filled and no empty space', palette: 'black ink only', framing: 'The drawing running to a thin white margin on every side'},
    ],
    directions: ['dragon', 'space', 'forest'],
    directionHint: 'try: dragon',
    seo: {
        title: 'Detailed coloring pages: owl, old town, ocean reef',
        metaDescription: 'Nine detailed coloring pages: a zentangle owl, a crowded old-town street and an ocean reef, in hair-thin, tangled and crowded styles.',
        h1: 'Detailed coloring pages: zentangle owl, old town street, ocean reef',
        tagline: 'Pages packed with small closed shapes, for hours of work with fine-tip pens.',
        intro: 'Detailed coloring pages for adults and teens who want hours of fine work with fine-tip pens. The nine pages show a zentangle owl, a crowded old-town street and an ocean reef, packed with small closed shapes. The styles are hair-thin, tangled fills and crowded. Type a different subject, such as a dragon or a forest, and the same style carries it.',
        useCases: [
            'a long fine-tip pen project for an adult',
            "a teen's focus activity after school",
            'a page to finish over several evenings',
            'a practice page for pattern work',
        ],
        faq: [
            {q: 'What is the difference between the styles?', a: 'Hair-thin uses a fine uniform line with hundreds of tiny closed cells. Tangled fills each area with a repeating tangle pattern, and crowded fills every corner of a busy drawing.'},
            {q: 'Which pen should I use?', a: 'The pages are drawn for fine-tip pens, because most of the shapes are small and closed.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. A subject you type, such as a dragon, a space scene or a forest, is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color.'},
        ],
    },
}
